import React, { useEffect, useState } from "react";
import { XIcon as X, DownloadSimpleIcon as DownloadSimple, ShareIcon as Share, PlusIcon as Plus, DogIcon as Dog } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/**
 * "Add Armstrong K9 to your home screen" install nudge.
 *
 * Strategy:
 *   • **Chrome / Edge / Samsung Internet / Android** browsers fire the
 *     `beforeinstallprompt` event when the app is installable. We
 *     intercept it (preventDefault), stash the deferred prompt, and
 *     surface our own polished banner. Click "Install" → call
 *     `prompt()` and resolve the user's choice.
 *   • **iOS Safari (iPhone / iPad)** does not fire `beforeinstallprompt`
 *     and there's no programmatic install API. Instead we detect Safari
 *     iOS, and on the user's second visit (≥ 1 prior session in last 7
 *     days) we show an instructions card: "Tap the Share button, then
 *     'Add to Home Screen'."
 *   • Dismissals + installs are persisted in `localStorage` so we never
 *     re-nag once the user has acted.
 *   • To keep first-visit UX clean and avoid annoying buyers casually
 *     browsing the marketplace, the banner only appears on the user's
 *     SECOND visit within a rolling 7-day window. Visit-count is bumped
 *     once per browser session (capped on a sessionStorage flag) so
 *     navigating within the same tab doesn't inflate the count.
 *
 * Visual: slides up from the bottom-right corner on desktop, full-width
 * pill at the bottom of the viewport on mobile. Dismissible. Auto-hides
 * on standalone-mode launches (i.e., when the user is already inside
 * the installed PWA).
 */

const STORAGE_KEYS = {
  visitCount: "armstrong-k9-visit-count",
  lastVisit: "armstrong-k9-last-visit",
  dismissedAt: "armstrong-k9-pwa-dismissed-at",
  installed: "armstrong-k9-pwa-installed",
  sessionMarked: "armstrong-k9-session-counted",
};

const DAYS_BEFORE_RENUDGE = 30;
const VISIT_WINDOW_DAYS = 7;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const safari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome/.test(ua);
  return iOS && safari;
}

function bumpVisitCount() {
  try {
    if (sessionStorage.getItem(STORAGE_KEYS.sessionMarked)) return;
    sessionStorage.setItem(STORAGE_KEYS.sessionMarked, "1");
    const now = Date.now();
    const last = parseInt(localStorage.getItem(STORAGE_KEYS.lastVisit) || "0", 10);
    const tooOld = now - last > VISIT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    const prevCount = tooOld
      ? 0
      : parseInt(localStorage.getItem(STORAGE_KEYS.visitCount) || "0", 10);
    localStorage.setItem(STORAGE_KEYS.visitCount, String(prevCount + 1));
    localStorage.setItem(STORAGE_KEYS.lastVisit, String(now));
  } catch {
    /* localStorage disabled — silently skip */
  }
}

function shouldShow() {
  try {
    if (isStandalone()) return false;
    if (localStorage.getItem(STORAGE_KEYS.installed)) return false;
    const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEYS.dismissedAt) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < DAYS_BEFORE_RENUDGE * 24 * 60 * 60 * 1000) {
      return false;
    }
    const visitCount = parseInt(localStorage.getItem(STORAGE_KEYS.visitCount) || "0", 10);
    return visitCount >= 2;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [variant, setVariant] = useState(null); // "android" | "ios" | null
  const [visible, setVisible] = useState(false);
  // Never nag inside the native app — they already installed it.
  const inNative = typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.();

  useEffect(() => {
    if (inNative) return;
    bumpVisitCount();

    const onBeforeInstall = (e) => {
      // Chromium fires this once and only once per page-load — capture
      // it so we can defer to a moment when the user is more engaged.
      e.preventDefault();
      setDeferredPrompt(e);
      if (shouldShow()) {
        setVariant("android");
        // Tiny delay so the banner doesn't pop the instant the page
        // hydrates — let the page settle first.
        setTimeout(() => setVisible(true), 1500);
      }
    };

    const onInstalled = () => {
      try {
        localStorage.setItem(STORAGE_KEYS.installed, "1");
      } catch {}
      setVisible(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari path — no `beforeinstallprompt`, so we feature-detect.
    if (isIOSSafari() && shouldShow()) {
      setVariant("ios");
      setTimeout(() => setVisible(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [inNative]);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.dismissedAt, String(Date.now()));
    } catch {}
    setVisible(false);
  };

  const triggerInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        try {
          localStorage.setItem(STORAGE_KEYS.installed, "1");
        } catch {}
      } else {
        try {
          localStorage.setItem(STORAGE_KEYS.dismissedAt, String(Date.now()));
        } catch {}
      }
      setDeferredPrompt(null);
      setVisible(false);
    } catch {
      // Stale prompt — fall back to dismissal.
      setVisible(false);
    }
  };

  if (!visible || !variant) return null;

  return (
    <div
      role="dialog"
      aria-label="Install Armstrong K9 Registry"
      data-testid="install-prompt"
      className="fixed inset-x-3 bottom-3 z-[100] sm:left-auto sm:right-6 sm:bottom-6 sm:w-[380px] rounded-2xl bg-white shadow-[0_20px_60px_-20px_rgba(14,14,14,0.45)] border border-[#E8E0CC] overflow-hidden animate-in slide-in-from-bottom-6 duration-500"
    >
      {/* Gold accent strip */}
      <div className="h-1 bg-gold-gradient" />

      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 h-7 w-7 rounded-full hover:bg-[#FAFAFA] flex items-center justify-center text-[#6B6B6B] transition-colors"
        data-testid="install-dismiss"
      >
        <X size={14} weight="bold" />
      </button>

      <div className="p-5 pb-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-11 w-11 rounded-xl bg-[#FBF7EC] border border-[#E8D9A6] flex items-center justify-center flex-shrink-0">
            <Dog size={22} weight="duotone" className="text-gold-dark" />
          </div>
          <div className="min-w-0">
            <p className="font-spectral text-[17px] leading-tight text-ink">
              Add Armstrong K9 to your home screen
            </p>
            <p className="text-[12px] text-[#6B6B6B] mt-0.5">
              One tap to track meals, walks, listings & inbox.
            </p>
          </div>
        </div>

        {variant === "android" ? (
          <div className="flex gap-2 mt-4">
            <Button
              onClick={triggerInstall}
              className="flex-1 rounded-full bg-ink hover:bg-crimson text-white h-10 text-[13px]"
              data-testid="install-accept"
            >
              <DownloadSimple size={14} weight="bold" className="mr-1.5" /> Install app
            </Button>
            <Button
              onClick={dismiss}
              variant="ghost"
              className="rounded-full h-10 text-[12px] text-[#6B6B6B]"
            >
              Not now
            </Button>
          </div>
        ) : (
          <div className="mt-3 space-y-2.5 text-[12.5px] text-[#3a3a3a] leading-relaxed">
            <p className="font-medium text-ink">To install on iPhone:</p>
            <ol className="space-y-1.5 pl-1">
              <li className="flex items-start gap-2">
                <span className="font-bold text-gold-dark">1.</span>
                <span>
                  Tap the <Share size={14} weight="bold" className="inline -mt-0.5 mx-0.5" /> Share button at the bottom of Safari.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-gold-dark">2.</span>
                <span>
                  Scroll down and tap <strong>Add to Home Screen</strong> <Plus size={13} weight="bold" className="inline -mt-0.5 ml-0.5" />.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-gold-dark">3.</span>
                <span>Tap <strong>Add</strong> in the top right.</span>
              </li>
            </ol>
            <button
              onClick={dismiss}
              className="text-[11px] text-[#9A9A9A] hover:text-ink underline underline-offset-2 pt-1"
            >
              Got it, hide this
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
